import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Minus, Plus, Trash2, ShoppingBag, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatUSD } from "@/context/CartContext";
import { api, createCheckoutSession } from "@/lib/api";

export default function Cart() {
    const {
        items,
        subtotal,
        donation,
        total,
        donate,
        setDonate,
        updateQty,
        removeItem,
    } = useCart();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [giftNote, setGiftNote] = useState("");
    const [notePrices, setNotePrices] = useState({ silver: 1, golden: 1.75 });
    useEffect(() => {
        api.get("/public/gift-note-prices").then(({ data }) => setNotePrices(data)).catch(() => {});
    }, []);
    const noteText = giftNote.trim();
    const noteTier = !noteText ? null : noteText.length <= 200 ? "Silver Note" : "Golden Note";
    const noteFee = !noteText ? 0 : noteText.length <= 200 ? notePrices.silver : notePrices.golden;
    const [wrap, setWrap] = useState(false);
    const wrapFee = wrap ? notePrices.wrap ?? 4 : 0;

    const onCheckout = async () => {
        if (items.length === 0) return;
        setLoading(true);
        try {
            const payload = {
                items: items.map((l) => ({
                    slug: l.slug,
                    quantity: l.quantity,
                })),
                donate,
                origin_url: window.location.origin,
                customer_email: email || undefined,
                gift_note: giftNote.trim() || undefined,
                gift_wrap: wrap,
            };
            const { url } = await createCheckoutSession(payload);
            window.location.href = url;
        } catch (e) {
            console.error(e);
            // Insider Preview Drop · 48h waitlist-only gate
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "early_access_required") {
                const ends = detail.early_access_ends_at
                    ? new Date(detail.early_access_ends_at).toLocaleString()
                    : "soon";
                toast.error(
                    email
                        ? `${email} isn't on the insider list yet. Join the waitlist to shop before ${ends}.`
                        : `Insider preview active — enter your waitlist email to shop. Open to everyone after ${ends}.`,
                    { duration: 7000 },
                );
            } else {
                toast.error("Could not start checkout. Please try again.");
            }
            setLoading(false);
        }
    };

    return (
        <div className="bg-cream min-h-screen" data-testid="cart-page">
            <section className="max-w-6xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-24">
                <span className="divider-rule text-brown">Your Basket</span>
                <h1 className="font-serif text-5xl sm:text-6xl text-forest mt-5">
                    Basket <span className="italic text-gold">&amp;</span>{" "}
                    Checkout
                </h1>

                {items.length === 0 ? (
                    <div
                        className="mt-16 text-center py-20 border-t border-b border-forest/10"
                        data-testid="cart-page-empty"
                    >
                        <ShoppingBag
                            size={32}
                            className="mx-auto text-brown/60"
                        />
                        <p className="font-serif text-3xl text-forest mt-6">
                            Nothing cured in here yet.
                        </p>
                        <Link
                            to="/shop"
                            className="btn-pill btn-primary mt-8 inline-flex"
                        >
                            Shop The Glow Set
                        </Link>
                    </div>
                ) : (
                    <div className="mt-12 grid lg:grid-cols-12 gap-10">
                        {/* Items */}
                        <div className="lg:col-span-7">
                            <ul className="border-t border-forest/10">
                                {items.map((item) => (
                                    <li
                                        key={item.slug}
                                        className="py-6 flex gap-5 border-b border-forest/10"
                                        data-testid={`cart-page-line-${item.slug}`}
                                    >
                                        <Link
                                            to={`/shop/${item.slug}`}
                                            className="w-28 h-36 overflow-hidden bg-cream-warm rounded-sm shrink-0"
                                        >
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </Link>
                                        <div className="flex-1 flex flex-col">
                                            <div className="flex justify-between gap-3">
                                                <div>
                                                    <Link
                                                        to={`/shop/${item.slug}`}
                                                        className="font-serif text-2xl text-forest hover:text-gold transition-colors"
                                                    >
                                                        {item.name}
                                                    </Link>
                                                    <p className="text-xs text-brown mt-1 tracking-wide">
                                                        4.5 oz ·{" "}
                                                        {formatUSD(item.price)}{" "}
                                                        each
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        removeItem(item.slug)
                                                    }
                                                    className="text-brown/60 hover:text-forest transition-colors"
                                                    aria-label="Remove"
                                                    data-testid={`cart-page-remove-${item.slug}`}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                            <div className="mt-auto flex items-center justify-between">
                                                <div className="inline-flex items-center border border-forest/30 rounded-full">
                                                    <button
                                                        onClick={() =>
                                                            updateQty(
                                                                item.slug,
                                                                item.quantity -
                                                                    1,
                                                            )
                                                        }
                                                        className="w-9 h-9 flex items-center justify-center text-forest hover:text-gold"
                                                        aria-label="Decrease"
                                                    >
                                                        <Minus size={14} />
                                                    </button>
                                                    <span className="w-9 text-center font-serif text-forest">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            updateQty(
                                                                item.slug,
                                                                item.quantity +
                                                                    1,
                                                            )
                                                        }
                                                        className="w-9 h-9 flex items-center justify-center text-forest hover:text-gold"
                                                        aria-label="Increase"
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>
                                                <span className="font-serif text-xl text-forest">
                                                    {formatUSD(
                                                        item.price *
                                                            item.quantity,
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Summary */}
                        <aside
                            className="lg:col-span-5 lg:sticky lg:top-28 self-start bg-cream-warm p-8 rounded-sm border border-forest/10"
                            data-testid="cart-page-summary"
                        >
                            <h2 className="font-serif text-2xl text-forest">
                                Summary
                            </h2>

                            <label
                                className="mt-6 flex items-start gap-3 p-4 rounded-sm border border-gold/50 bg-cream cursor-pointer"
                                data-testid="cart-page-donate-label"
                            >
                                <input
                                    type="checkbox"
                                    checked={donate}
                                    onChange={(e) => setDonate(e.target.checked)}
                                    className="mt-1 h-4 w-4 accent-[color:var(--ig-gold)]"
                                    data-testid="cart-page-donate-toggle"
                                />
                                <span className="text-sm text-forest leading-relaxed">
                                    <span className="font-semibold tracking-wide uppercase text-[0.7rem] text-gold mr-1">
                                        Round-Up · 10%
                                    </span>
                                    Round up your order 10% for{" "}
                                    <span className="font-serif italic">
                                        CyberKids Academy
                                    </span>{" "}
                                    — we forward every cent.
                                </span>
                            </label>

                            <div className="mt-6">
                                <label className="text-[0.65rem] tracking-[0.3em] uppercase text-brown" htmlFor="cart-gift-note">
                                    Gift note (optional) · Silver Note {formatUSD(notePrices.silver)} up to 200 characters · Golden Note {formatUSD(notePrices.golden)} up to 500
                                </label>
                                <textarea
                                    id="cart-gift-note"
                                    rows={3}
                                    maxLength={500}
                                    value={giftNote}
                                    onChange={(e) => setGiftNote(e.target.value)}
                                    placeholder="A short note for the receipt…"
                                    className="mt-2 w-full bg-transparent border-b border-forest/30 py-2 text-forest outline-none"
                                    data-testid="cart-gift-note-input"
                                />
                                <p className="text-xs text-brown text-right" data-testid="cart-gift-note-count">
                                    {noteTier ? `${noteTier} · ` : ""}{giftNote.length}/{giftNote.length > 200 ? 500 : 200}
                                </p>
                                <label className="mt-3 flex items-center gap-3 text-sm text-forest cursor-pointer" data-testid="cart-gift-wrap-label">
                                    <input type="checkbox" checked={wrap} onChange={(e) => setWrap(e.target.checked)} data-testid="cart-gift-wrap-toggle" />
                                    {/* Stock photo — replace with Ymaku's own wrap photo before launch */}
                                    <img src="https://images.pexels.com/photos/16276565/pexels-photo-16276565.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop"
                                        alt="Banana-leaf gift wrap" className="w-12 h-12 object-cover rounded-sm" data-testid="cart-gift-wrap-photo" />
                                    Banana-leaf gift wrap · {formatUSD(notePrices.wrap ?? 4)}
                                </label>
                            </div>

                            <div className="mt-6">
                                <label className="text-[0.65rem] tracking-[0.3em] uppercase text-brown">
                                    Email for order updates
                                </label>
                                <input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-2 w-full bg-cream border-b border-forest/30 focus:border-gold py-2 outline-none text-forest"
                                    data-testid="cart-page-email-input"
                                />
                            </div>

                            <div className="mt-6 space-y-2 text-sm text-forest">
                                <div className="flex justify-between">
                                    <span className="text-brown/80">
                                        Subtotal
                                    </span>
                                    <span
                                        className="font-medium"
                                        data-testid="cart-page-subtotal"
                                    >
                                        {formatUSD(subtotal)}
                                    </span>
                                </div>
                                {donate && (
                                    <div
                                        className="flex justify-between text-gold"
                                        data-testid="cart-page-donation"
                                    >
                                        <span>CyberKids Round-Up</span>
                                        <span>{formatUSD(donation)}</span>
                                    </div>
                                )}
                                {noteTier && (
                                    <div className="flex justify-between" data-testid="cart-page-note-fee">
                                        <span>{noteTier}</span>
                                        <span>{formatUSD(noteFee)}</span>
                                    </div>
                                )}
                                {wrap && (
                                    <div className="flex justify-between" data-testid="cart-page-wrap-fee">
                                        <span>Banana-leaf gift wrap</span>
                                        <span>{formatUSD(wrapFee)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-brown/70">
                                    <span>Shipping</span>
                                    <span>Calculated at checkout</span>
                                </div>
                                <div className="flex justify-between pt-3 mt-3 border-t border-forest/15 font-serif text-xl">
                                    <span>Total</span>
                                    <span data-testid="cart-page-total">
                                        {formatUSD(total + noteFee + wrapFee)}
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={onCheckout}
                                disabled={loading}
                                className="btn-pill btn-primary w-full justify-center mt-8 disabled:opacity-50"
                                data-testid="cart-page-checkout-btn"
                            >
                                {loading ? (
                                    "Preparing Checkout…"
                                ) : (
                                    <>
                                        <Lock size={14} /> Secure Checkout
                                    </>
                                )}
                            </button>
                            <p className="mt-4 text-center text-[0.65rem] tracking-[0.3em] uppercase text-brown/60">
                                Zero-Cap · Stripe Secured · Aloha
                            </p>
                        </aside>
                    </div>
                )}
            </section>
        </div>
    );
}
